import type { makeTrpcClient } from '@tg/app-core';
import type { QueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { observable } from '@trpc/server/observable';
import { dayKey, rebaseDays, rebasePack, synth, whoKey, type DayRow, type Pack, type Who } from './days';
import { pendingOps, serverRows } from './outbox';
import { isOnline, reportNetFail, reportNetOk } from './net';

/* Звено tRPC перед отправкой на сервер.
   Связь есть — запрос идёт как обычно; ответ с днями получает сверху правки, ещё лежащие в очереди
   (иначе свежий ответ сервера «откатил» бы их на экране до отправки).
   Связи нет — запрос не ждёт сеть: ответ из данных на устройстве, а действие, которому нужен
   сервер, сразу получает понятную ошибку. */

type Link = NonNullable<Parameters<typeof makeTrpcClient>[0]>[number];

const NO_NET = 'Нет связи с сервером — это станет доступно, когда появится интернет';
const isNetErr = (e: unknown) => e instanceof TRPCClientError && !e.data && !(e.shape as unknown);

/** Версии дней из свежего ответа сервера — очереди оффлайна (до наложения её правок) */
function noteRevs(path: string, input: unknown, data: unknown) {
  if (path === 'trainer.days') {
    const i = input as { who: Who; from: string; to: string };
    serverRows(
      (data as DayRow[]).map((r) => ({ key: dayKey(i.who, r.date), date: r.date, rev: r.rev })),
      { who: whoKey(i.who), from: i.from, to: i.to },
    );
  } else if (path === 'trainer.offlinePack') {
    const p = data as Pack;
    serverRows(p.days.map((r) => ({ key: dayKey(r.clientId ? { clientId: r.clientId } : { groupId: r.groupId! }, r.date), date: r.date, rev: r.rev })));
  }
}

function rebase(path: string, input: unknown, data: unknown) {
  const ops = pendingOps();
  if (!ops.length) return data;
  if (path === 'trainer.days') return rebaseDays(data as DayRow[], input as Parameters<typeof rebaseDays>[1], ops);
  if (path === 'trainer.offlinePack') return rebasePack(data as Pack, ops);
  return data;
}

export const offlineLink =
  (qc: QueryClient): Link =>
  () =>
  ({ op, next }) =>
    observable((observer) => {
      /* Ответ с устройства — тоже с правками очереди сверху: кэш пишется раз в секунду, а очередь — сразу,
         и после перезагрузки без сети последняя правка не должна пропасть с экрана */
      const local = () => {
        if (op.type !== 'query') return undefined;
        const d = synth(qc, op.path, op.input);
        return d === undefined ? d : rebase(op.path, op.input, d);
      };
      if (!isOnline()) {
        const d = local();
        if (d !== undefined) {
          observer.next({ result: { type: 'data', data: d } });
          observer.complete();
        } else observer.error(TRPCClientError.from(new Error(NO_NET)));
        return;
      }
      const sub = next(op).subscribe({
        next(v) {
          reportNetOk();
          const r = v.result as { type?: string; data?: unknown };
          if (op.type === 'query' && r && 'data' in r) {
            noteRevs(op.path, op.input, r.data);
            observer.next({ ...v, result: { ...r, data: rebase(op.path, op.input, r.data) } as typeof v.result });
          }
          else observer.next(v);
        },
        error(e) {
          if (isNetErr(e)) {
            reportNetFail();
            const d = local();
            if (d !== undefined) {
              observer.next({ result: { type: 'data', data: d } });
              observer.complete();
              return;
            }
            observer.error(TRPCClientError.from(new Error(NO_NET)));
            return;
          }
          observer.error(e);
        },
        complete() {
          observer.complete();
        },
      });
      return () => sub.unsubscribe();
    });
